import { useCallback, useEffect, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DataTable, EmptyState, InlineStack, Layout, Page, Pagination, Select, Text, TextField, Thumbnail } from "@shopify/polaris";
import { NoteIcon } from "@shopify/polaris-icons";
import agent, { PreviewAtdProductSummary } from "../../app/api/agent";

export default function PreviewAtdProductsPage({ token, onUnauthorized, onLogout }: { token: string; onUnauthorized: () => void; onLogout: () => void; }) {
  const [products, setProducts] = useState<PreviewAtdProductSummary[]>([]);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseError = useCallback((error: any, fallback: string) => {
    if (error?.response?.status === 401) onUnauthorized();
    return String(error?.response?.data || error?.message || fallback);
  }, [onUnauthorized]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await agent.PreviewAtd.products(token, page, pageSize, {
        query,
        brand,
        inStock: inStockOnly ? true : undefined,
        isActive: activeOnly ? true : undefined,
      });

      setProducts(result.items);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      setError(parseError(error, "Unable to load ATD products."));
    } finally {
      setLoading(false);
    }
  }, [activeOnly, brand, inStockOnly, page, pageSize, parseError, query, token]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const rows = products.map((item) => {
    const imageSource = item.primaryImageUrl && item.primaryImageUrl.trim().length > 0 ? item.primaryImageUrl : NoteIcon;

    return [
      <Thumbnail source={imageSource} alt={item.productName || item.partNumber || "ATD product"} size="small" />,
      item.partNumber || "—",
      item.productName || "—",
      item.brandName || "—",
      item.categoryName || "—",
      `${item.availableQuantity ?? 0}`,
      item.isDiscontinued ? <Badge tone="warning">Discontinued</Badge> : (item.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="critical">Inactive</Badge>),
      item.lastCatalogSyncAt ? new Date(item.lastCatalogSyncAt).toLocaleString() : "—",
    ];
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageSizeOptions = [
    { label: "25", value: "25" },
    { label: "50", value: "50" },
    { label: "100", value: "100" },
    { label: "250", value: "250" },
  ];

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Page title="TrailOps ATD Product Browser" subtitle="Read-only view of synced ATD catalog products" secondaryActions={[{ content: "Logout", onAction: onLogout }]}> 
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack gap="300" align="start" blockAlign="end" wrap>
                <div style={{ minWidth: 240 }}>
                  <TextField label="Search" value={query} onChange={setQuery} autoComplete="off" placeholder="Part #, product name, category" />
                </div>
                <div style={{ minWidth: 220 }}>
                  <TextField label="Brand" value={brand} onChange={setBrand} autoComplete="off" />
                </div>
                <Checkbox label="In-stock only" checked={inStockOnly} onChange={setInStockOnly} />
                <Checkbox label="Active only" checked={activeOnly} onChange={setActiveOnly} />
                <div style={{ minWidth: 160 }}>
                  <Select
                    label="Page size"
                    options={pageSizeOptions}
                    value={String(pageSize)}
                    onChange={(value) => {
                      setPageSize(Number(value));
                      setPage(1);
                    }}
                  />
                </div>
                <Button variant="primary" onClick={() => { setPage(1); loadProducts(); }} loading={loading}>Search</Button>
              </InlineStack>

              {error ? <Banner tone="critical" title="Unable to load products"><p>{error}</p></Banner> : null}

              {products.length === 0 && !loading ? (
                <EmptyState heading="No ATD products found" image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png">
                  <p>Try broadening your search or clearing one of the filters.</p>
                </EmptyState>
              ) : (
                <>
                  <InlineStack align="space-between">
                    <Text as="p" tone="subdued">Showing {products.length} of {totalCount} products.</Text>
                    <Button onClick={loadProducts} loading={loading}>Refresh</Button>
                  </InlineStack>
                  <DataTable
                    columnContentTypes={["text", "text", "text", "text", "text", "numeric", "text", "text"]}
                    headings={["Image", "Part #", "Product", "Brand", "Category", "Available", "Status", "Catalog Sync"]}
                    rows={rows}
                  />
                </>
              )}

              <InlineStack align="end">
                <Pagination
                  hasPrevious={page > 1}
                  hasNext={page < totalPages}
                  onPrevious={() => goToPage(Math.max(1, page - 1))}
                  onNext={() => goToPage(Math.min(totalPages, page + 1))}
                  label={`Page ${page} of ${totalPages}`}
                />
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
